import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Sidebar, Topbar } from '../components';
import { NotificationProvider } from '../contexts/NotificationContext';
import { PhaseProvider } from '../contexts/PhaseContext';
import MobileNav from '../components/MobileNav';
import { clearCurrentUser, getCurrentUser } from '../data/session';
import { onTokenChange, routeAfterAuth } from '../services/auth-service';
import { auth } from '../config/firebase';

const AppShell: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const user = getCurrentUser();
  const noSidebar = user.role === 'mentor' || user.role === 'investor' || user.role === 'partner' || user.role === 'associate';
  return (
    <div className="h-screen overflow-hidden bg-bg text-text flex">
      <MobileNav />
      {!noSidebar && <Sidebar expanded={sidebarOpen} onToggle={() => setSidebarOpen(o => !o)} />}
      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
        <Topbar />
        <div className="flex-1 overflow-auto">
          <div className="max-w-7xl mx-auto px-6 py-6 w-full">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
};

const AppLayout = () => {
  const navigate = useNavigate();
  const user = getCurrentUser();
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    auth.authStateReady().then(() => setAuthReady(true));
  }, []);

  useEffect(() => {
    if (!authReady) return;
    if (!user.id) {
      navigate('/login');
      return;
    }
    // routeAfterAuth handles every gate: pending/rejected → /pending,
    // founder-without-plan → /select-plan, partner/associate/investor with
    // missing role data → /complete-profile.
    const target = routeAfterAuth(user as any);
    if (!target.startsWith('/app/')) navigate(target);
  }, [authReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync app session with Firebase token state. We only react to a transition
  // from "had a Firebase token" → "no token" (sign-out, revoked refresh token).
  // The very first event on mount may report null even for valid sessions
  // (demo accounts with no Firebase user, or before persistence rehydrates),
  // so we ignore it.
  useEffect(() => {
    let hadToken = false;
    const unsub = onTokenChange((fbUser) => {
      if (fbUser) {
        hadToken = true;
        return;
      }
      if (hadToken) {
        clearCurrentUser();
        navigate('/login');
      }
    });
    return () => unsub();
  }, [navigate]);

  if (!authReady) {
    return (
      <div className="h-screen flex items-center justify-center bg-bg">
        <div className="w-8 h-8 border-2 border-chart border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const shell = (
    <NotificationProvider>
      <AppShell />
    </NotificationProvider>
  );

  if (user.role === 'founder') {
    return <PhaseProvider>{shell}</PhaseProvider>;
  }

  return shell;
};

export default AppLayout;
